<template>
    <bs5-base-modal title="Edit User" :visible="visible" @close="closeModal">
        <template v-slot:default>
            <form @submit.prevent="updateUser">
                <div class="mb-3">
                    <bs5-base-label for="fullName">Full Name</bs5-base-label>
                    <bs5-base-input v-model="user.fullName" type="text" id="fullName" required />
                </div>

                <div class="mb-3">
                    <bs5-base-label for="email" class="form-label">Email</bs5-base-label>
                    <bs5-base-input v-model="user.email" type="email" id="email" required />
                </div>

                <div class="mb-3">
                    <bs5-base-label for="role" class="form-label">Role</bs5-base-label>
                    <select v-model.number="user.role" class="form-select" id="role">
                        <option v-for="(label, value) in userRoles" :key="value" :value="value">
                            {{ label }}
                        </option>
                    </select>
                </div>
            </form>
        </template>

        <template v-slot:footer>
            <bs5-cancel-button  @click="closeModal"/>
            <bs5-save-button :loading="saving" @click="updateUser" />
        </template>
    </bs5-base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from "vue";
import userService from "@/services/user.service";
import { User } from "@/models/user.model"
import { UserRole } from "@/enums/user-role.enum";
import { showToast } from "@/utils/toast";

export default defineComponent({
    name: "EditUserModal",
    props: {
        visible: { type: Boolean, required: true },
        userId: { type: String, required: true },
    },
    emits: ["close", "user-updated"],
    data() {
        return {
            user: ref<User>({
                id: "",
                fullName: "",
                email: "",
                role: UserRole.Client, // Domyślnie Client (jako liczba)
            }),
            saving: ref(false), // ✅ Stan ładowania
            userRoles: {
                [UserRole.SuperAdmin]: "Super Admin",
                [UserRole.Distributor]: "Distributor",
                [UserRole.Client]: "Client",
            },
        };
    },
    watch: {
        userId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.fetchUser();
                }
            },
        },
    },
    methods: {
        async fetchUser() {
            try {
                const response = await userService.getUserById(this.userId);
                this.user = response;
            } catch (error) {
                console.error("Error fetching user:", error);
            }
        },
        async updateUser() {
            this.saving = true;
            try {
                await userService.updateUser(this.user.id as string, {
                    fullName: this.user.fullName,
                    email: this.user.email,
                    role: this.user.role,
                });
                this.$emit("user-updated");
                showToast("User edit successfully!", "success");
                this.closeModal();
            } catch (error) {
                showToast("Failed to add user", "error");
                console.error("Error updating user:", error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
    mounted() {
        if (this.userId) {
            this.fetchUser();
        }
    },
});
</script>